'use client';

import { useState, useEffect } from 'react';
import { VariationStyle, VariantSessionCreate, Series } from '@/lib/types';
import api from '@/lib/api';
import LlmPicker, { useLlmChoice } from '../LlmPicker';

interface VariantGenerationFormProps {
  onGenerate: (request: VariantSessionCreate) => Promise<void>;
  isLoading: boolean;
  series: Series[];
  selectedSeriesId: number | null;
}

const VARIATION_STYLES: { value: VariationStyle; label: string; description: string }[] = [
  {
    value: 'refine',
    label: 'Refine',
    description: 'Subtle variations, stay close to the original concept',
  },
  {
    value: 'explore',
    label: 'Explore',
    description: 'Balanced creativity with meaningful variations',
  },
  {
    value: 'surprise',
    label: 'Surprise',
    description: 'Wild and unexpected interpretations',
  },
];

// What each backend is good for, shown under the picker
const MODEL_HINTS: Record<'qwen' | 'dolphin' | 'qwen_uncensored', string> = {
  qwen: 'Qwen3.6 35B — widest variation, refuses adult concepts',
  dolphin: 'Dolphin 8B — uncensored, but variations run repetitive',
  qwen_uncensored: 'Qwen3.6 35B Uncensored — uncensored at full 35B quality',
};

// Collapsible section component
function CollapsibleSection({
  title,
  isOpen,
  onToggle,
  children,
  badge,
}: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <div className="border border-[var(--border-subtle)] rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-[var(--text-secondary)]">{title}</span>
          {badge}
        </div>
        <svg
          className={`w-5 h-5 text-[var(--text-muted)] transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && <div className="p-4 border-t border-[var(--border-subtle)]">{children}</div>}
    </div>
  );
}

export default function VariantGenerationForm({
  onGenerate,
  isLoading,
  series,
  selectedSeriesId,
}: VariantGenerationFormProps) {
  const [baseDescription, setBaseDescription] = useState('');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [variationStyle, setVariationStyle] = useState<VariationStyle>('explore');
  const [variationCount, setVariationCount] = useState(10);
  const [ignoreSeriesContext, setIgnoreSeriesContext] = useState(false);
  const [seriesContext, setSeriesContext] = useState<{ name: string; count: number } | null>(null);

  // Which LLM writes the prompts. Auto picks the uncensored dolphin in private mode;
  // qwen_uncensored is the 35B uncensored option when dolphin's 8B output is too flat.
  const { choice: llmChoice, resolved: resolvedLlm, change: handleLlmChange, confirmGpu } =
    useLlmChoice('variants-llm');

  // Collapsible state
  const [showBaseDescription, setShowBaseDescription] = useState(true);
  const [showAdditionalInstructions, setShowAdditionalInstructions] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  // Fetch series context when selectedSeriesId changes
  useEffect(() => {
    if (selectedSeriesId && !ignoreSeriesContext) {
      api.getSeriesContext(selectedSeriesId)
        .then((ctx) => {
          setSeriesContext({ name: ctx.series_name, count: ctx.prompt_count });
        })
        .catch(() => {
          setSeriesContext(null);
        });
    } else {
      setSeriesContext(null);
    }
  }, [selectedSeriesId, ignoreSeriesContext]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!baseDescription.trim()) return;
    if (!(await confirmGpu(resolvedLlm))) return; // covers auto-resolution too

    await onGenerate({
      base_description: baseDescription.trim(),
      additional_instructions: additionalInstructions.trim() || undefined,
      variation_style: variationStyle,
      variation_count: variationCount,
      series_id: selectedSeriesId,
      ignore_series_context: ignoreSeriesContext,
      llm: resolvedLlm,
    });
  };

  // Truncate text for badge display
  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Base Description - Collapsible */}
      <CollapsibleSection
        title="Base Description"
        isOpen={showBaseDescription}
        onToggle={() => setShowBaseDescription(!showBaseDescription)}
        badge={
          baseDescription && !showBaseDescription ? (
            <span className="text-xs text-[var(--text-muted)] truncate max-w-[300px]">
              {truncateText(baseDescription, 50)}
            </span>
          ) : null
        }
      >
        <div className="space-y-2">
          <textarea
            value={baseDescription}
            onChange={(e) => setBaseDescription(e.target.value)}
            className="input min-h-[120px] resize-none text-base"
            placeholder="Describe what you want to generate... (e.g., 'a serene mountain landscape at sunset with dramatic clouds')"
            required
            minLength={10}
            maxLength={2000}
          />
          <p className="text-xs text-[var(--text-muted)]">
            AI will generate {variationCount} unique prompt variations based on this description
          </p>
        </div>
      </CollapsibleSection>

      {/* Additional Instructions - Collapsible */}
      <CollapsibleSection
        title="Additional Instructions"
        isOpen={showAdditionalInstructions}
        onToggle={() => setShowAdditionalInstructions(!showAdditionalInstructions)}
        badge={
          additionalInstructions && !showAdditionalInstructions ? (
            <span className="text-xs text-[var(--text-muted)] truncate max-w-[300px]">
              {truncateText(additionalInstructions, 40)}
            </span>
          ) : (
            <span className="text-xs text-[var(--text-muted)]">(optional)</span>
          )
        }
      >
        <textarea
          value={additionalInstructions}
          onChange={(e) => setAdditionalInstructions(e.target.value)}
          className="input min-h-[80px] resize-none text-sm"
          placeholder="Any specific style, mood, or elements to include/exclude... (e.g., 'focus on warm colors, avoid people')"
          maxLength={1000}
        />
      </CollapsibleSection>

      {/* Variation Style - Always visible */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-[var(--text-secondary)]">
          Variation Style
        </label>
        <div className="grid grid-cols-3 gap-3">
          {VARIATION_STYLES.map((style) => (
            <button
              key={style.value}
              type="button"
              onClick={() => setVariationStyle(style.value)}
              className={`p-4 rounded-xl text-left transition-all cursor-pointer ${
                variationStyle === style.value
                  ? 'bg-[var(--accent)] text-black'
                  : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              <span className="block font-medium">{style.label}</span>
              <span
                className={`block text-xs mt-1 ${
                  variationStyle === style.value ? 'text-black/70' : 'text-[var(--text-muted)]'
                }`}
              >
                {style.description}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Advanced Settings - Collapsible */}
      <CollapsibleSection
        title="Advanced Settings"
        isOpen={showAdvancedSettings}
        onToggle={() => setShowAdvancedSettings(!showAdvancedSettings)}
        badge={
          <span className="text-xs text-[var(--text-muted)]">
            {variationCount} variations
            {seriesContext && !ignoreSeriesContext ? ` • ${seriesContext.name}` : ''}
          </span>
        }
      >
        <div className="space-y-6">
          {/* Series Context Info */}
          {selectedSeriesId && seriesContext && (
            <div className="p-4 rounded-xl bg-[var(--bg-primary)] border border-[var(--border-subtle)]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[var(--text-secondary)]">
                    Series Context: {seriesContext.name}
                  </p>
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    AI will avoid {seriesContext.count} existing prompt{seriesContext.count !== 1 ? 's' : ''} in this series
                  </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ignoreSeriesContext}
                    onChange={(e) => setIgnoreSeriesContext(e.target.checked)}
                    className="w-4 h-4 rounded border-[var(--border-subtle)] text-[var(--accent)] focus:ring-[var(--accent)]"
                  />
                  <span className="text-sm text-[var(--text-muted)]">Ignore</span>
                </label>
              </div>
            </div>
          )}

          {/* Variation Count */}
          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-muted)]">Number of Variations</span>
              <span className="text-[var(--text-primary)] font-medium">{variationCount}</span>
            </label>
            <input
              type="range"
              value={variationCount}
              onChange={(e) => setVariationCount(parseInt(e.target.value))}
              min={4}
              max={20}
              step={1}
              className="w-full accent-[var(--accent)] cursor-pointer"
            />
            <div className="flex justify-between text-xs text-[var(--text-muted)]">
              <span>4</span>
              <span>20</span>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Prompt Model - Always visible: it decides both quality and what gets refused */}
      <div className="flex items-center justify-between gap-3 p-4 border border-[var(--border-subtle)] rounded-xl">
        <div className="min-w-0">
          <p className="text-sm font-medium text-[var(--text-secondary)]">Prompt Model</p>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">{MODEL_HINTS[resolvedLlm]}</p>
        </div>
        <LlmPicker
          choice={llmChoice}
          resolved={resolvedLlm}
          onChange={handleLlmChange}
          disabled={isLoading}
          className="shrink-0 text-sm bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-[var(--text-secondary)] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          title="Model that writes the prompt variations — Auto uses the uncensored Dolphin in private mode, Qwen otherwise"
        />
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading || !baseDescription.trim() || baseDescription.length < 10}
        className="btn btn-primary w-full h-12 text-base font-semibold"
      >
        {isLoading ? (
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Generating Variations...
          </span>
        ) : (
          `Generate ${variationCount} Variations`
        )}
      </button>
    </form>
  );
}
